import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { InsightAction, InsightChatAnswer, InsightDecision, InsightSummary } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { confirm } from "./ConfirmDialog";
import { toast } from "../react/hooks/use-toast";

// DecisionCard — renders the `<decision/>` cockpit element. After the record
// loads it calls POST /api/insights/evaluate and shows the deterministic
// decision (score ring · severity chip · ranked factors · recommended-action
// buttons). The decision is produced by the backend; the LLM never makes it.
//
// Recommended actions NEVER auto-run: clicking one prompts a confirm (when the
// action requires it) and only then dispatches via /api/insights/run-action.

interface DecisionCardProps {
    modelKey: string;
    recordRef: string | null;
    title?: string | null;
}

const SEVERITY_CLASS: Record<string, string> = {
    LOW: "decision-card--low",
    MEDIUM: "decision-card--medium",
    HIGH: "decision-card--high",
};

export function DecisionCard({ modelKey, recordRef, title }: DecisionCardProps): ReactNode {
    const runtime = useRuntime();
    const [decision, setDecision] = useState<InsightDecision | null>(null);
    const [narration, setNarration] = useState<InsightSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busyRef, setBusyRef] = useState<string | null>(null);
    const [question, setQuestion] = useState("");
    const [chatAnswer, setChatAnswer] = useState<InsightChatAnswer | null>(null);
    const [chatBusy, setChatBusy] = useState(false);

    const evaluate = useCallback(async () => {
        if (!recordRef) return;
        setLoading(true);
        setError(null);
        try {
            const d = await runtime.services.insights.evaluate(modelKey, recordRef);
            setDecision(d);
            // Narration is a best-effort overlay — deterministic decision renders regardless.
            runtime.services.insights
                .summarize(modelKey, recordRef)
                .then((s) => setNarration(s.available ? s : null))
                .catch(() => setNarration(null));
        } catch (e) {
            // No profile for this model is an expected, quiet state — the card
            // simply doesn't render. Any other failure surfaces inline.
            const msg = e instanceof Error ? e.message : String(e);
            if (/profile/i.test(msg) && /not found|no active/i.test(msg)) {
                setDecision(null);
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    }, [runtime, modelKey, recordRef]);

    useEffect(() => {
        void evaluate();
    }, [evaluate]);

    const onAction = useCallback(
        async (action: InsightAction) => {
            if (!recordRef) return;
            if (action.requires_confirm) {
                const ok = await confirm({
                    title: action.label,
                    message: `Run "${action.label}" on this record?`,
                    confirmLabel: "Confirm",
                    variant: action.severity === "HIGH" ? "danger" : "default",
                });
                if (!ok) return;
            }
            setBusyRef(action.ref);
            try {
                await runtime.services.insights.runAction(
                    modelKey,
                    recordRef,
                    action.action_kind,
                    action.ref,
                );
                toast({ title: "Action executed", description: action.label });
                // Re-evaluate — the action may have changed the inputs.
                await evaluate();
            } catch (e) {
                toast({
                    title: "Action failed",
                    description: e instanceof Error ? e.message : String(e),
                    variant: "destructive",
                });
            } finally {
                setBusyRef(null);
            }
        },
        [runtime, modelKey, recordRef, evaluate],
    );

    const onAsk = useCallback(async () => {
        const q = question.trim();
        if (!recordRef || !q || chatBusy) return;
        setChatBusy(true);
        try {
            const ans = await runtime.services.insights.chat(modelKey, recordRef, q);
            setChatAnswer(ans);
        } catch {
            setChatAnswer({ available: false, answer: "Chat is unavailable right now.", ai: false });
        } finally {
            setChatBusy(false);
        }
    }, [runtime, modelKey, recordRef, question, chatBusy]);

    // Unsaved record → nothing to decide yet.
    if (!recordRef) return null;
    if (loading && !decision) {
        return <div className="decision-card decision-card--low">
            <span className="decision-card__muted">Evaluating…</span>
        </div>;
    }
    if (error) {
        return <div className="decision-card decision-card--high">
            <span className="decision-card__error">Decision unavailable: {error}</span>
        </div>;
    }
    if (!decision) return null;

    const pct = Math.round(decision.score * 100);
    const severityClass = SEVERITY_CLASS[decision.severity] ?? "decision-card--low";
    const topFactors = decision.factors.slice(0, 5);

    return (
        <div className={`decision-card ${severityClass}`}>
            <div className="decision-card__head">
                <div className="decision-card__ring" style={{ ["--ring-pct" as string]: String(pct) }}>
                    <div className="decision-card__ring-inner">{pct}</div>
                </div>
                <div className="decision-card__title">{title || "Decision"}</div>
                <span className="decision-card__chip">{decision.severity}</span>
            </div>

            {narration?.summary && (
                <div className="decision-card__narration">
                    <span className="decision-card__narration-tag">{narration.ai ? "AI" : "Auto"}</span>
                    <span className="decision-card__narration-text">{narration.summary}</span>
                </div>
            )}

            {topFactors.length > 0 && (
                <div className="decision-card__factors">
                    {topFactors.map((f) => (
                        <div className="decision-card__factor" key={`${f.label}-${f.ref}`}>
                            <span className="decision-card__factor-label" title={f.label}>{f.label}</span>
                            <span className="decision-card__bar">
                                <span
                                    className="decision-card__bar-fill"
                                    style={{ ["--bar-pct" as string]: `${Math.round(Math.min(Math.max(f.normalized, 0), 1) * 100)}%` }}
                                />
                            </span>
                            <span className="decision-card__factor-val">
                                {f.unsupported_kind ? "—" : f.contribution.toFixed(2)}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {decision.actions.length > 0 && (
                <div className="decision-card__actions">
                    {decision.actions.map((a) => (
                        <button
                            type="button"
                            key={`${a.ref}-${a.action_kind}`}
                            className={`btn btn--sm ${a.severity === "HIGH" ? "btn--negative" : ""}`}
                            disabled={busyRef !== null}
                            onClick={() => void onAction(a)}
                        >
                            {busyRef === a.ref ? "…" : a.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="decision-card__chat">
                <div className="decision-card__chat-row">
                    <input
                        className="decision-card__chat-input"
                        type="text"
                        placeholder="Ask about this decision…"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") void onAsk();
                        }}
                    />
                    <button
                        type="button"
                        className="btn btn--sm"
                        disabled={chatBusy || !question.trim()}
                        onClick={() => void onAsk()}
                    >
                        {chatBusy ? "…" : "Ask"}
                    </button>
                </div>
                {chatAnswer?.answer && (
                    <div className="decision-card__chat-answer">{chatAnswer.answer}</div>
                )}
            </div>
        </div>
    );
}
